import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Truck, ShoppingCart, Package, BarChart2, Settings, FileText,
  TrendingUp, Calendar, Users, Award, MapPin, ArrowUp, ArrowDown,
  PieChart, ClipboardList, Building2, ArrowLeftRight,
  AlertTriangle, CheckCircle2, Clock, DollarSign, Filter,
  Search, Download, RefreshCw, ChevronRight, Plus, Eye,
  Activity, Target, Zap, Shield, Globe, Database, MoreHorizontal,
  ChevronDown
} from 'lucide-react';

// Import tab components
import OverviewTab from './components/OverviewTab';
import PurchaseRequestsTab from './components/PurchaseRequestsTab';
import ProcurementTab from './components/ProcurementTab';
import LogisticsTab from './components/LogisticsTab';
import WarehouseInventoryTab from './components/WarehouseInventoryTab';
import PlanningForecastingTab from './components/PlanningForecastingTab';
import ReportsKPIsTab from './components/ReportsKPIsTab';
import SettingsTab from './components/SettingsTab';
import RequestDetail from './components/RequestDetail';

const SupplyChainDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Main tabs (visible in the tab bar)
  const mainTabs = [
    { 
      id: 'overview', 
      label: 'Overview', 
      icon: Activity,
      description: 'Executive dashboard & KPIs'
    },
    { 
      id: 'purchase-requests', 
      label: 'Purchase Requests', 
      icon: ClipboardList,
      description: 'Create & track internal requests'
    },
    { 
      id: 'procurement', 
      label: 'Procurement', 
      icon: ShoppingCart,
      description: 'Vendor management & PO processing'
    },
    { 
      id: 'logistics', 
      label: 'Logistics', 
      icon: Truck,
      description: 'Inbound, outbound & returns'
    },
    { 
      id: 'warehouse-inventory', 
      label: 'Warehouse & Inventory', 
      icon: Package,
      description: 'Stock tracking & management'
    },
    { 
      id: 'planning-forecasting', 
      label: 'Planning & Forecasting', 
      icon: TrendingUp,
      description: 'Demand planning & analysis'
    }
  ];

  // Additional tabs (in the More dropdown)
  const moreTabs = [
    { 
      id: 'reports-kpis', 
      label: 'Reports & KPIs', 
      icon: BarChart2,
      description: 'Analytics & performance metrics'
    },
    { 
      id: 'settings', 
      label: 'Settings', 
      icon: Settings,
      description: 'Configuration & workflows'
    }
  ];

  // All tabs combined for routing
  const allTabs = [...mainTabs, ...moreTabs];

  // Sync activeTab with current URL on mount
  useEffect(() => {
    const path = window.location.pathname;
    const pathSegments = path.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    
    // If we're on a detail page, don't change the activeTab
    if (path.includes('/purchase-requests/')) {
      setActiveTab('purchase-requests');
      return;
    }
    
    // Set activeTab based on URL
    if (lastSegment && lastSegment !== 'supply-chain') {
      setActiveTab(lastSegment);
    }
  }, []);

  // Handle clicking outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowMoreDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setShowMoreDropdown(false);
    navigate(`/supply-chain/${tabId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-xl p-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold">Supply Chain Management</h1>
            <p className="text-emerald-100 mt-1">
              End-to-end supply chain visibility and optimization
            </p>
          </div>
        </div>

        {/* Centered Tabs */}
        <div className="mt-6 flex justify-center">
          <div className="inline-flex space-x-1 bg-white/10 rounded-lg p-1 backdrop-blur-sm">
            {/* Main tabs */}
            {mainTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-emerald-700'
                      : 'text-white/90 hover:bg-white/10'
                  }`}
                  title={tab.description}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}

            {/* More dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowMoreDropdown(!showMoreDropdown)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  moreTabs.some(tab => tab.id === activeTab)
                    ? 'bg-white text-emerald-700'
                    : 'text-white/90 hover:bg-white/10'
                }`}
              >
                <MoreHorizontal className="w-4 h-4" />
                More
                <ChevronDown className={`w-3 h-3 transition-transform ${showMoreDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown menu */}
              {showMoreDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                >
                  {moreTabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => handleTabClick(tab.id)}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-sm font-medium transition-colors ${
                          activeTab === tab.id
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        title={tab.description}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Routes for Tabs */}
      <Routes>
        <Route path="/" element={<OverviewTab />} />
        <Route path="/overview" element={<OverviewTab />} />
        <Route path="/purchase-requests" element={<PurchaseRequestsTab />} />
        <Route path="/purchase-requests/:id" element={<RequestDetail />} />
        <Route path="/procurement" element={<ProcurementTab />} />
        <Route path="/logistics" element={<LogisticsTab />} />
        <Route path="/warehouse-inventory" element={<WarehouseInventoryTab />} />
        <Route path="/planning-forecasting" element={<PlanningForecastingTab />} />
        <Route path="/reports-kpis" element={<ReportsKPIsTab />} />
        <Route path="/settings" element={<SettingsTab />} />
      </Routes>
    </div>
  );
};

export default SupplyChainDashboard; 